import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Strings from '../../../Strings';
import { actions } from '../../SuperUser/Patients/redux/actions';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import {
  DELETE_CUSTOM_TASK_RESULT,
  DISABLE_ORG_TASK_RESULT,
  ENABLE_ORG_TASK_RESULT,
  GET_TASKS_LIST_ALL_RESULT,
  GET_TASKS_LIST_RESULT,
} from '../../SuperUser/Patients/redux/constants';
import { showStatus } from '../../../utils';
import { ActionDropdownOnOffMenu, ActionWidget } from '../../../components/ActionWidget';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { openModalAction } from '../../../actions/modal';
import { CUSTOM_WORK_TYPE_MODAL_NAME } from '../../../modals/CustomWorkTypeModal';
import { PageHeader } from '../../../components/PageHeader';
import { TextInput } from '../../../components/PageHeader/TextInput';

export const getTimerDefaultTaskRowActions = ({ setAsDefaultTaskForTimer, clearDefaultTaskForTimer }) => [
  {
    icon: 'launch',
    onClick: (_, row) => setAsDefaultTaskForTimer(row),
    text: Strings.setAsDefaultTaskForTimer,
    visible: (_, row) => !row.isTimerDefault,
  },
  {
    icon: 'delete',
    onClick: (_, row) => clearDefaultTaskForTimer(row),
    text: Strings.clearDefaultTaskForTimer,
    visible: (_, row) => !!row.isTimerDefault,
  },
];

export const getSetTimerDefaultTaskConfirmation = (organizationId, task, previousDefaultTask, load) => ({
  caption: Strings.confirm,
  title: (
    <span>
      {Strings.formatString(
        Strings.setAsDefaultTaskForTimerWarning,
        task.taskName,
        previousDefaultTask?.taskName || 'unknown task'
      )}
    </span>
  ),
  onConfirmAction: actions.setTimerDefaultTask(organizationId, {taskMetadataId : task.id}),
  onCancelAction: null,
  hideCaution: true,
  confirmPostAction: load,
});

export const getClearTimerDefaultTaskConfirmation = (organizationId, task, load) => ({
  caption: Strings.confirm,
  title: Strings.formatString(Strings.clearDefaultTaskForTimerWarning, task.taskName),
  onConfirmAction: actions.setTimerDefaultTask(organizationId, {taskMetadataId: null}),
  onCancelAction: null,
  hideCaution: true,
  confirmPostAction: load,
  onSuccessNotification: Strings.timerDefaultTaskCleared,
});

export const getWorkTypesColumns = () => [
  columnHelper.accessor('id', {
    header: Strings.id,
    size: 75,
  }),
  columnHelper.accessor('taskName', {
    header: Strings.capPatient.taskName,
    size: 500,
  }),
  columnHelper.accessor('enabled', {
    header: Strings.enabled,
    cell: info => showStatus(info.getValue()),
    size: 100,
  }),
  columnHelper.accessor('mandatory', {
    header: Strings.mandatoryTasks,
    cell: info => showStatus(!!info.getValue()),
    size: 100,
  }),
  columnHelper.accessor('isTimerDefault', {
    header: Strings.isTimerDefaultTask,
    cell: info => showStatus(!!info.getValue()),
    size: 100,
  }),
  columnHelper.accessor('orgSpecific', {
    header: 'Org specific',
    cell: info => showStatus(!!info.getValue()),
    size: 100,
  }),
  columnHelper.accessor('allowDuration', {
    header: Strings.tracksTime,
    cell: info => showStatus(info.getValue() !== false),
    size: 100,
  }),
];

// Saves per organization, in click order. Module-level so the order survives leaving and
// re-opening the tab: an enable still in flight from the previous visit must reach the server
// before a disable clicked after coming back. A failed save doesn't block later ones.
const orgTaskSaveQueues = new Map();

export function enqueueOrgTaskSave(organizationId, save) {
  const previous = orgTaskSaveQueues.get(organizationId) || Promise.resolve();
  const next = previous.then(save, save);
  const tail = next.catch(() => {});
  orgTaskSaveQueues.set(organizationId, tail);
  tail.then(() => {
    if (orgTaskSaveQueues.get(organizationId) === tail) {
      orgTaskSaveQueues.delete(organizationId);
    }
  });
  return next;
}

function WorkTypes(props) {
  const { enableOrgTask, disableOrgTask, getTasks, getAllTasks, deleteCustomTask, openCustomWorkTypeModal, openConfirmModal, organizationId, showNotification } = props;
  const [tasks, setTasks] = React.useState([]);
  const [allTasks, setAllTasks] = React.useState([]);
  const [selectedTasks, setSelectedTasks] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');

  const tableRef = React.useRef(null);
  const tasksRef = React.useRef(tasks);
  tasksRef.current = tasks;

  const load = React.useCallback(() => {
    getAllTasks(organizationId).then(resp => {
      if (resp?.type === GET_TASKS_LIST_ALL_RESULT) {
        setAllTasks(resp?.response?.data || []);
      }
    });
    // `latest`: a refetch started before a later save is cancelled, so it can't show a stale list.
    getTasks(organizationId).then(resp => {
      if (resp?.type === GET_TASKS_LIST_RESULT) {
        setTasks(resp.response?.data || []);
      }
    });
    tableRef?.current?.resetSelectedRows();
  }, [getAllTasks, getTasks, organizationId]);

  React.useEffect(() => {
    load();
  }, [organizationId, load]);

  const enabledTaskIds = React.useMemo(() => new Set(tasks.map(task => task.id)), [tasks]);

  const mandatoryTaskIds = React.useMemo(() => new Set(
    tasks.filter(task => task.mandatory).map(task => task.id),
  ), [tasks]);

  // Each task is saved on its own (PUT enables, DELETE disables), so toggles made before the
  // previous refetch landed can't undo each other the way a full-list replace did.
  const changeTasksValue = React.useCallback((taskIds, value) => {
    const enable = value === 1;
    if (taskIds.length === 0 || taskIds.every(taskId => enabledTaskIds.has(taskId) === enable)) return;
    if (!enable && taskIds.some(taskId => mandatoryTaskIds.has(taskId))) return;
    enqueueOrgTaskSave(organizationId, async () => {
      const responses = await Promise.all(
        taskIds.map(taskId => (enable ? enableOrgTask : disableOrgTask)(organizationId, taskId)),
      );
      const failed = responses.find(resp => resp?.type !== (enable ? ENABLE_ORG_TASK_RESULT : DISABLE_ORG_TASK_RESULT));
      if (failed) {
        showNotification(failed.response?.data?.standard_error?.message || 'Unable to save task changes', 5000, true);
      } else if (responses.length > 0) {
        showNotification(Strings.formatString(Strings.itemSaved, Strings.workTypes));
      }
      load();
    }).catch(() => {
      // Never let a rejection poison the chain: later toggles must still work.
      showNotification('Unable to save task changes', 5000, true);
    });
  }, [enabledTaskIds, mandatoryTaskIds, enableOrgTask, disableOrgTask, organizationId, showNotification, load]);

  const columns = React.useMemo(getWorkTypesColumns, []);
  
  const dataForTable = React.useMemo(() => {
    return allTasks
      .map(task => {
        const selected = tasks.find(t => t.id === task.id);
        return {
          ...task,
          enabled: !!selected,
          mandatory: mandatoryTaskIds.has(task.id),
          isTimerDefault: selected?.defaultForAutoStartTimer,
        };
      })
      .filter(task => (task.taskName || '').toLowerCase().includes(searchQuery.trim().toLowerCase()))
      .sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
        return (a.taskName || '').localeCompare(b.taskName || '');
      });
  }, [allTasks, tasks, searchQuery, mandatoryTaskIds]);

  const handleRowSelectionChange = React.useCallback((rows) => {
    setSelectedTasks(rows.map(r => r.id));
  }, []);

  const openCreateTaskModal = React.useCallback(() => {
    openCustomWorkTypeModal({
      mode: 'create',
      orgId: organizationId,
      allTasks,
      orgTasks: tasks,
      onSuccess: load,
    });
  }, [openCustomWorkTypeModal, organizationId, load, allTasks, tasks]);

  const openEditTaskModal = React.useCallback((taskRow) => {
    openCustomWorkTypeModal({
      mode: 'editOrgTask',
      orgId: organizationId,
      allTasks,
      orgTasks: tasks,
      task: {
        ...taskRow,
        org_uuid: taskRow.orgSpecific ? organizationId : '',
      },
      onSuccess: load,
    });
  }, [openCustomWorkTypeModal, organizationId, allTasks, tasks, load]);

  const setAsDefaultTaskForTimer = React.useCallback((task) => {
    const previousDefaultTask = tasksRef.current.find(t => t.defaultForAutoStartTimer);
    openConfirmModal(getSetTimerDefaultTaskConfirmation(organizationId, task, previousDefaultTask, load));
  }, [openConfirmModal, load, tasks, organizationId]);

  const clearDefaultTaskForTimer = React.useCallback((task) => {
    openConfirmModal(getClearTimerDefaultTaskConfirmation(organizationId, task, load));
  }, [openConfirmModal, organizationId, load]);

  const hasMandatorySelection = selectedTasks.some(taskId => mandatoryTaskIds.has(taskId));
  const allTasksEnabled = selectedTasks.length > 0 && selectedTasks.every(taskId => enabledTaskIds.has(taskId));
  const allTasksDisabled = selectedTasks.length > 0 && selectedTasks.every(taskId => !enabledTaskIds.has(taskId));

  const actionBtns = React.useMemo(() => (
    <ActionDropdownOnOffMenu
      img="action-edit"
      tooltiptext={Strings.billing.tasks}
      off={{
        label: Strings.formatString(Strings.actionOptionOff, Strings.billing.tasks),
        disabled: hasMandatorySelection || allTasksDisabled,
        disabledReason: hasMandatorySelection ? Strings.cannotDisableMandatoryTasks
          : allTasksDisabled ? Strings.allSelectedTasksDisabled : undefined,
      }}
      on={{
        label: Strings.formatString(Strings.actionOptionOn, Strings.billing.tasks),
        disabled: allTasksEnabled,
        disabledReason: allTasksEnabled ? Strings.allSelectedTasksEnabled : undefined,
      }}
      action={value => changeTasksValue(selectedTasks, value)}
    />
  ), [changeTasksValue, selectedTasks, hasMandatorySelection, allTasksEnabled, allTasksDisabled]);

  const tableButtons = React.useMemo(() => [
    ...getTimerDefaultTaskRowActions({setAsDefaultTaskForTimer, clearDefaultTaskForTimer}),
    {
      icon: 'edit',
      onClick: (_, row) => openEditTaskModal(row),
      text: Strings.edit,
      visible: (_, row) => row.orgSpecific,
    },
    {
      icon: 'delete',
      visible: (_, row) => Boolean(row.orgSpecific),
      onClick: (_, row) => {
        deleteCustomTask({ task_id: row.id }).then(resp => {
          if (resp?.type === DELETE_CUSTOM_TASK_RESULT) {
            showNotification('Task deleted');
            load();
          } else {
            showNotification(resp?.response?.data?.standard_error?.message || 'Unable to delete task', 5000, true);
          }
        });
      },
      text: Strings.delete,
    },
  ], [openEditTaskModal, deleteCustomTask, showNotification, load, tasks, organizationId, setAsDefaultTaskForTimer, clearDefaultTaskForTimer]);

  return (
    <>
      <PageHeader
        left={Strings.workTypes}
        right={(
          <>
            <TextInput
              placeholder={Strings.search}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              onClick={openCreateTaskModal}
              title={Strings.add}
            />
          </>
        )}
      />
      <Table3
        data={dataForTable}
        columns={columns}
        name="WorkTypes"
        buttons={tableButtons}
        enableColumnFiltering
        pinFirstColumn
        enableRowSelection
        onRowSelectionChange={handleRowSelectionChange}
        ref={tableRef}
      />
      <ActionWidget show={selectedTasks?.length > 0}>{actionBtns}</ActionWidget>
    </>
  );
}

WorkTypes.propTypes = {
  getAllTasks: PropTypes.func.isRequired,
  getTasks: PropTypes.func.isRequired,
  enableOrgTask: PropTypes.func.isRequired,
  disableOrgTask: PropTypes.func.isRequired,
  deleteCustomTask: PropTypes.func.isRequired,
  openCustomWorkTypeModal: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = {
  getAllTasks: actions.getAllTasks,
  getTasks: orgId => actions.getTasks(orgId, { latest: true }),
  enableOrgTask: actions.enableOrgTask,
  disableOrgTask: actions.disableOrgTask,
  deleteCustomTask: actions.deleteCustomTask,
  openCustomWorkTypeModal: data => openModalAction(CUSTOM_WORK_TYPE_MODAL_NAME, data),
  openConfirmModal: data => openModalAction('confirmation-modal', data),
  showNotification: notificationActions.show,
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkTypes);
